import { backendClient } from '../lib/api/backendClient';

export interface FavoriteRecording {
  id: string;
  recordingId: string;
  workId?: string;
  savedAt: string;
  notes?: string;
  labels: string[];
}

const toIsoString = (value?: string | number | Date | null): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) return undefined;
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
  }
  return undefined;
};

const normalizeLabels = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
      }
    } catch {
      return [value.trim()];
    }
  }
  return [];
};

const stringOrUndefined = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const deserializeFavoriteRecording = (payload: any): FavoriteRecording => {
  const savedAt = toIsoString(payload?.savedAt ?? payload?.saved_at);
  const fallback =
    stringOrUndefined(payload?.updated_at) ??
    stringOrUndefined(payload?.created_at) ??
    new Date().toISOString();

  const recordingId =
    stringOrUndefined(payload?.recordingId) ??
    stringOrUndefined(payload?.recording_id) ??
    stringOrUndefined(payload?.id) ??
    '';

  const workId =
    stringOrUndefined(payload?.workId) ??
    stringOrUndefined(payload?.work_id) ??
    undefined;

  return {
    id: stringOrUndefined(payload?.id) ?? recordingId,
    recordingId,
    workId,
    notes: stringOrUndefined(payload?.notes),
    savedAt: savedAt ?? fallback,
    labels: normalizeLabels(payload?.labels),
  };
};

export const favoriteRecordingsService = {
  async listFavorites(): Promise<FavoriteRecording[]> {
    const { data } = await backendClient.get<{ recordings?: any[] }>('/favorites/recordings');
    const recordings = Array.isArray(data?.recordings) ? data.recordings : [];
    return recordings.map(deserializeFavoriteRecording);
  },

  async saveFavorite(
    recordingId: string,
    options?: {
      workId?: string;
      notes?: string;
      labels?: string[];
      savedAt?: string | number | Date;
      id?: string;
    }
  ): Promise<FavoriteRecording> {
    if (!recordingId) {
      throw new Error('recordingId is required');
    }

    const payload: Record<string, unknown> = { recordingId };
    if (options?.workId) payload.workId = options.workId;
    if (options?.notes) payload.notes = options.notes;
    if (options?.labels && options.labels.length > 0) payload.labels = options.labels;
    if (options?.savedAt !== undefined) {
      const iso = toIsoString(options.savedAt);
      if (iso) payload.savedAt = iso;
    }
    if (options?.id) payload.id = options.id;

    const { data } = await backendClient.post<{ recording: any }>('/favorites/recordings', payload);
    return deserializeFavoriteRecording(data.recording);
  },

  async removeFavorite(recordingId: string): Promise<void> {
    if (!recordingId) return;
    await backendClient.delete(`/favorites/recordings/${encodeURIComponent(recordingId)}`);
  },

  async saveFavoritesBatch(
    recordingIds: string[],
    options?: {
      workId?: string;
      notes?: string;
      labels?: string[];
      savedAt?: string | number | Date;
    }
  ): Promise<{
    recordings: FavoriteRecording[];
    processed: number;
    saved: number;
    skipped: number;
  }> {
    const uniqueIds = Array.from(
      new Set(
        recordingIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0)
      )
    );

    if (!uniqueIds.length) {
      return { recordings: [], processed: 0, saved: 0, skipped: 0 };
    }

    const payload: Record<string, unknown> = {
      recordingIds: uniqueIds,
    };

    if (options?.workId) payload.workId = options.workId;
    if (options?.notes) payload.notes = options.notes;
    if (Array.isArray(options?.labels) && options.labels.length > 0) {
      payload.labels = options.labels;
    }
    if (options?.savedAt !== undefined) {
      const iso = toIsoString(options.savedAt);
      if (iso) payload.savedAt = iso;
    }

    const { data } = await backendClient.post<{
      recordings?: any[];
      processed?: number;
      saved?: number;
      skipped?: number;
    }>('/favorites/recordings/batch', payload);

    const recordings = Array.isArray(data?.recordings)
      ? data.recordings.map(deserializeFavoriteRecording)
      : [];

    const processed = typeof data?.processed === 'number' ? data.processed : uniqueIds.length;
    const saved = typeof data?.saved === 'number' ? data.saved : recordings.length;
    const skipped =
      typeof data?.skipped === 'number'
        ? data.skipped
        : Math.max(processed - saved, 0);

    return { recordings, processed, saved, skipped };
  },

  async removeFavoritesBatch(recordingIds: string[]): Promise<number> {
    const uniqueIds = Array.from(
      new Set(
        recordingIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0)
      )
    );

    if (!uniqueIds.length) {
      return 0;
    }

    const { data } = await backendClient.delete<{ deleted?: number }>('/favorites/recordings', {
      data: { recordingIds: uniqueIds },
    });

    const deleted =
      typeof data?.deleted === 'number' && Number.isFinite(data.deleted) ? data.deleted : 0;
    return deleted;
  },

  async isFavorited(recordingId: string): Promise<boolean> {
    if (!recordingId) return false;
    try {
      const favorites = await favoriteRecordingsService.listFavorites();
      return favorites.some((favorite) => favorite.recordingId === recordingId);
    } catch {
      return false;
    }
  },
};

export default favoriteRecordingsService;
